import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const [plan, setPlan] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPlan(data.plan || "");
          await refreshUser();
          setState("success");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) {
        if (attempts >= MAX_POLLS) { setState("error"); return; }
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, refreshUser]);

  return (
    <div className="cc cc-bg min-h-screen flex items-center justify-center text-white px-5" data-testid="payment-success-page">
      <div className="cc-card rounded-2xl p-10 max-w-md w-full text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 size={40} className="mx-auto animate-spin text-blue-400" />
            <h1 className="cc-heading text-2xl font-700 mt-5">Confirming your payment…</h1>
            <p className="text-slate-400 text-sm mt-2">This only takes a few seconds.</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="payment-success">
            <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
            <h1 className="cc-heading text-2xl font-700 mt-5">You're all set!</h1>
            <p className="text-slate-400 text-sm mt-2">
              Your {plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : ""} plan is now active. Welcome aboard.
            </p>
            <button
              onClick={() => navigate("/credit/app/billing")}
              data-testid="payment-success-continue"
              className="cc-btn-primary text-white rounded-xl px-6 py-2.5 text-sm font-500 mt-6 w-full">
              Go to Billing
            </button>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-pending">
            <XCircle size={44} className="mx-auto text-amber-400" />
            <h1 className="cc-heading text-2xl font-700 mt-5">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-slate-400 text-sm mt-2">
              {state === "timeout"
                ? "Your payment is taking a little longer than usual. It will reflect on your billing page shortly."
                : "We couldn't confirm this payment. If you were charged, it will be reflected on your billing page."}
            </p>
            <Link to="/credit/app/billing"
              className="inline-block cc-btn-primary text-white rounded-xl px-6 py-2.5 text-sm font-500 mt-6 w-full"
              data-testid="payment-pending-continue">
              Back to Billing
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
